/*
 * Copyright (c) 2022. American Express Company
 */

// theirs
import React, { Component, ReactComponentElement } from 'react';

// ours
import { ReportBarViewModel } from 'src/types/view-models/report-bar.view.model';
import Anchor from '../atomic/atoms/anchor/Anchor';
import { TooltipPlacement } from 'src/types/tooltip-placement.enum';

// styles
import styles from './ReportBar.module.scss';
import Typography from '../typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import MoreInfoTooltip from 'src/components/more-info-tooltip/MoreInfoTooltip';
import Selectors from 'src/utils/selectors';

export interface ReportBarState {
    barWidth: string;
    showBar: boolean;
}

export default class ReportBar extends Component<ReportBarViewModel, ReportBarState> {
    public override state = {
        barWidth: '0px',
        showBar: false
    };

    public override componentDidMount(): void {
        this.handleChange();
    }

    public override componentDidUpdate(prevProps: ReportBarViewModel): void {
        if (this.props.percentage !== prevProps.percentage) {
            this.handleChange();
        }
    }

    public override render(): ReactComponentElement<any> {
        const barStyle: { width: string; background: string; display: string } = {
            width: this.state.barWidth,
            background: this.props.color,
            display: this.state.showBar ? '' : 'none'
        };

        const title: JSX.Element = this.props.onClick ? (
            <Anchor
                onClick={() => {
                    this.props.onClick();
                }}
                data-cy={Selectors.PlanAhead.REPORT_BAR_TITLE}
                typographyProps={{
                    className: styles.titleBlue,
                    variant: TypographyVariants.BODY
                }}
            >
                {this.props.title}
            </Anchor>
        ) : (
            <Typography
                className={this.props.forceBlue ? styles.titleBlue : styles.titleDefault}
                variant={TypographyVariants.BODY}
                data-cy={Selectors.PlanAhead.REPORT_BAR_TITLE}
            >
                {this.props.title}
            </Typography>
        );

        return (
            <div className={styles.reportBar} data-cy={Selectors.PlanAhead.REPORT_BAR}>
                <div className={styles.topRow}>
                    <div className={styles.left}>
                        <div className={styles.imgTitle}>
                            {this.props.img}
                            {title}
                        </div>
                        {this.props.tooltipDescription ? (
                            <MoreInfoTooltip
                                body={this.props.tooltipDescription}
                                placement={TooltipPlacement.BOTTOM}
                                className={styles.infoIcon}
                                buttonAriaLabel={this.props.title}
                            />
                        ) : null}
                    </div>
                    <div className={styles.right}>
                        <Typography className={styles.value} variant={TypographyVariants.BODY_BOLD}>
                            {this.props.value}
                        </Typography>
                        <Typography className={styles.percentage} variant={TypographyVariants.BODY}>
                            {this.props.percentage}
                        </Typography>
                    </div>
                </div>
                {this.state.showBar || this.props.children ? (
                    <div className={styles.bottomRow}>
                        <div style={barStyle} className={styles.computedBar} />
                        {this.props.children}
                    </div>
                ) : null}
            </div>
        );
    }

    private handleChange(): void {
        const showBar: boolean = parseInt(this.props.percentage) !== 0;
        this.setState({
            barWidth: this.props.percentage,
            showBar
        });
    }
}
